"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.refreshTokenKey = exports.otpTokenKey = exports.tokenUserIdKey = exports.tokenLicenseKey = exports.tokenEmailKey = exports.tokenRoleKey = exports.tokenUsernameKey = exports.persistentRoleKey = exports.externalTokenKey = exports.tokenKey = exports.ROLE_EXPIRATION = exports.DEFAULT_EXPIRATION = exports.DEFAULT_DOMAIN = void 0;
var jwt_decode_1 = __importDefault(require("jwt-decode"));
var js_cookie_1 = __importDefault(require("js-cookie"));
var date_fns_1 = require("date-fns");
/** The fallback domain is an empty string */
exports.DEFAULT_DOMAIN = '';
/** Seconds */
exports.DEFAULT_EXPIRATION = 3600;
/** Days */
exports.ROLE_EXPIRATION = 7;
/**
 * Keys used to store data inside the cookies
 * Note that the "token" encodes actual user data, while the "external token" is just a hash used
 * to authenticate users comming from external urls, and will probably throw an error if we try to pass
 * it through "decode".
 * This "extenral token" is mostly used to know how to redirect the user back to the page they came from.
 */
exports.tokenKey = 'user_data';
exports.externalTokenKey = 'external_token';
exports.persistentRoleKey = 'persistent_role';
/** Keys inside the parsed cookie that will identify user properties */
exports.tokenUsernameKey = 'identity/claims/name';
exports.tokenRoleKey = 'identity/claims/role';
exports.tokenEmailKey = 'identity/claims/emailaddress';
exports.tokenLicenseKey = 'identity/claims/serialnumber';
exports.tokenUserIdKey = 'sub';
exports.otpTokenKey = 'swi_access_token';
exports.refreshTokenKey = 'refresh_token';
var SessionManager = /** @class */ (function () {
    /** Empty private constructor */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    function SessionManager() {
        var _this = this;
        /** Last expiration used value */
        this.lastUsedExpiration = exports.DEFAULT_EXPIRATION;
        /** Last used domain */
        this.usedDomain = exports.DEFAULT_DOMAIN;
        /**
         * Sets the domain scope for the cookie where the session will be stored.
         *
         * @param domain - The base domain to use when registering the cookie
         */
        this.registerDomain = function (domain) {
            _this.usedDomain = domain;
        };
        /**
         * Logs in with the provided parameters.
         * The role is saved as a different key because it must outlive the token so that we can redirect the
         * user to the correct login screen.
         */
        this.login = function (_a) {
            var token = _a.token, externalToken = _a.externalToken, _b = _a.expiration, expiration = _b === void 0 ? exports.DEFAULT_EXPIRATION : _b;
            var cookieConfig = _this.makeCookieConfiguration(expiration);
            js_cookie_1.default.set(exports.tokenKey, token, cookieConfig);
            js_cookie_1.default.set(exports.persistentRoleKey, _this.getRoleFromIncomingToken(token), __assign(__assign({}, cookieConfig), { expires: exports.ROLE_EXPIRATION }));
            if (externalToken) {
                js_cookie_1.default.set(exports.externalTokenKey, externalToken, cookieConfig);
            }
            _this.lastUsedExpiration = expiration;
        };
        /**
         * Deletes the current registered paramerters and logs out.
         * Note that the role not removed, as it is needed to redirect to the correct authentication page
         */
        this.logout = function () {
            var cookieConfig = _this.makeCookieConfiguration(_this.lastUsedExpiration);
            js_cookie_1.default.remove(exports.tokenKey, cookieConfig);
            js_cookie_1.default.remove(exports.externalTokenKey, cookieConfig);
            _this.lastUsedExpiration = exports.DEFAULT_EXPIRATION;
        };
        /**
         * Returns the current registered token string. Will return an empty string if there
         * is nothing registered.
         */
        this.getToken = function () {
            return _this.getTokenString();
        };
        /**
         * @returns Returns the OTP authorization bearer if it exists. Returns an empty string otherwise.
         */
        this.getOtpToken = function () {
            var _a;
            return (_a = js_cookie_1.default.get(exports.otpTokenKey)) !== null && _a !== void 0 ? _a : '';
        };
        this.setOtpToken = function (token) {
            js_cookie_1.default.set(exports.otpTokenKey, token);
        };
        /**
         * Returns the current registered external token string. Will return an empty string if
         * there is nothing registered.
         */
        this.getExternalToken = function () {
            return _this.getExternalTokenString();
        };
        /**
         * Returns whether the user is logged in
         */
        this.isLoggedIn = function () {
            return typeof js_cookie_1.default.get(exports.tokenKey) !== 'undefined';
        };
        /**
         * Returns true if there is data present for the external token key. This signifies that the user
         * logged in via an external URL.
         */
        this.isExternalLoggedIn = function () {
            return typeof js_cookie_1.default.get(exports.externalTokenKey) !== 'undefined';
        };
        /**
         * Returns the name for the logged user.
         * Returns an empty string if no data is registered.
         */
        this.getLoggedUserName = function () {
            return _this.getValueFromToken(exports.tokenUsernameKey);
        };
        /**
         * Returns the role (basically, user type) for the logged user, or an empty string if nothing
         * is found.
         * Note that the role is saved as a separate cookie alongside the user, as
         */
        this.getLoggedRole = function () {
            return js_cookie_1.default.get(exports.persistentRoleKey) || '';
        };
        this.getLoggedEmail = function () {
            return _this.getValueFromToken(exports.tokenEmailKey);
        };
        /**
         * Return the license for the logged user
         */
        this.getLoggedLicense = function () {
            return _this.getValueFromToken(exports.tokenLicenseKey);
        };
        /**
         * Returns the user id for the logged user
         */
        this.getLoggedUserId = function () {
            return _this.getValueFromToken(exports.tokenUserIdKey);
        };
        this.getValueFromToken = function (value) {
            var parsedToken = _this.getParsedToken();
            var nameKey = Object.keys(parsedToken).find(function (key) {
                return key.endsWith(value);
            });
            return nameKey ? parsedToken[nameKey] : '';
        };
        /**
         * Tries to parse the registered token key, and returns an object.
         * Will return an empty object if something hapens while parsing the token.
         */
        this.getParsedToken = function () {
            try {
                var tokenString = _this.getTokenString();
                return jwt_decode_1.default(tokenString);
            }
            catch (e) {
                /** InvalidTokenError means that the token was malformed */
                return {};
            }
        };
        /**
         * Extracts the role from the provided token, or empty string if nothing is defined
         *
         * @param token - The token
         */
        this.getRoleFromIncomingToken = function (token) {
            try {
                var decodedToken = jwt_decode_1.default(token);
                var foundToken = Object.keys(decodedToken).find(function (curTokenKey) {
                    return curTokenKey.endsWith(exports.tokenRoleKey);
                });
                return foundToken ? decodedToken[foundToken] : '';
            }
            catch (e) {
                return '';
            }
        };
        /**
         * Returns the registered token string if present, or an empty string as a fallback
         */
        this.getTokenString = function () {
            return js_cookie_1.default.get(exports.tokenKey) || '';
        };
        /**
         * Returns the registered external token if present, or an empty string as a fallback
         */
        this.getExternalTokenString = function () {
            return js_cookie_1.default.get(exports.externalTokenKey) || '';
        };
        /**
         * Returns an object used to configure the Cookies.
         *
         * @param expiration - How long before the token expires
         */
        this.makeCookieConfiguration = function (expiration) {
            return {
                expires: date_fns_1.addSeconds(new Date(), expiration),
                domain: _this.usedDomain,
            };
        };
        /**
         * Returns the hostname without the subdomains
         *
         * @param hostNameResult - The hostname extracted from the window object
         */
        this.applicationDomain = function (hostNameResult) {
            var formattedHostname = '';
            var hostnameParts = hostNameResult.split('.');
            if (hostnameParts.length >= 3) {
                formattedHostname = hostnameParts.slice(1).join('.');
            }
            else {
                formattedHostname = hostNameResult;
            }
            return formattedHostname;
        };
        /**
         * @returns Returns the refresh token if it exists. Returns an empty string otherwise.
         */
        this.getRefreshToken = function () {
            return js_cookie_1.default.get(exports.refreshTokenKey) || '';
        };
        this.setRefreshToken = function (_a) {
            var token = _a.token, _b = _a.expiration, expiration = _b === void 0 ? exports.DEFAULT_EXPIRATION : _b;
            var cookieConfig = _this.makeCookieConfiguration(expiration);
            js_cookie_1.default.set(exports.refreshTokenKey, token, cookieConfig);
        };
        // empty constructor
    }
    /**
     * Singleton getter
     */
    SessionManager.getInstance = function () {
        if (!SessionManager.instance) {
            SessionManager.instance = new SessionManager();
        }
        return SessionManager.instance;
    };
    return SessionManager;
}());
exports.default = SessionManager;
//# sourceMappingURL=SessionManager.js.map